import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const brandPartners = [
  { name: "Domino's", category: "QSR", icon: "ri-restaurant-line" },
  { name: "McDonald's", category: "QSR", icon: "ri-restaurant-line" },
  { name: "Starbucks", category: "QSR", icon: "ri-cup-line" },
  { name: "Shell", category: "Fuel & Mobility", icon: "ri-gas-station-line" },
  { name: "BPCL", category: "Fuel & Mobility", icon: "ri-gas-station-line" },
  { name: "COCO", category: "Fuel & Mobility", icon: "ri-gas-station-line" },
  { name: "Amazon", category: "E-commerce", icon: "ri-shopping-bag-line" },
  { name: "Flipkart", category: "E-commerce", icon: "ri-shopping-bag-line" },
  { name: "Delhivery", category: "Logistics", icon: "ri-truck-line" },
  { name: "DMart", category: "Retail", icon: "ri-store-line" },
  { name: "Spencer's", category: "Retail", icon: "ri-store-line" },
  { name: "Reliance Smart", category: "Retail", icon: "ri-store-line" },
  { name: "Charge+Zone", category: "EV Charging", icon: "ri-flashlight-line" },
  { name: "Tata Power", category: "Solar & Energy", icon: "ri-sun-line" },
  { name: "Nykaa", category: "Retail", icon: "ri-store-line" },
  { name: "Pizza Hut", category: "QSR", icon: "ri-restaurant-line" },
];

const developerPartners = [
  { name: "Prestige Group", projects: "50+ JDAs", location: "South India" },
  { name: "Sobha Limited", projects: "30+ JDAs", location: "South India" },
  { name: "Brigade Group", projects: "40+ JDAs", location: "South India" },
  { name: "Puravankara", projects: "25+ JDAs", location: "Pan India" },
  { name: "Mahindra Lifespaces", projects: "20+ JDAs", location: "Pan India" },
  { name: "Assetz Property", projects: "15+ JDAs", location: "South India" },
];

export default function PartnersPage() {
  const { getContent } = usePageContent('partners');
  const heroBadge = stripHtml(getContent('partners-hero-badge')) || 'Network';
  const heroSub = stripHtml(getContent('partners-hero-sub')) || '200+ brands and developers actively seeking land through CashMyProperty.ai.';
  const brandsHeading = stripHtml(getContent('partners-brands-heading')) || '200+ Brands Looking for Land';
  const brandsSub = stripHtml(getContent('partners-brands-sub')) || 'Our Brand Match Algorithm connects your land directly with national and international brands actively expanding across India.';
  const devHeading = stripHtml(getContent('partners-developers-heading')) || 'Tier-1 Developers Ready to Build';
  const joinHeading = stripHtml(getContent('partners-join-heading')) || 'Want to Join Our Partner Network?';
  const ctaHeading = stripHtml(getContent('partners-cta-heading')) || 'Your Land Could Be Their Next Location';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="Our"
        highlight="Partners"
        subtitle={heroSub}
        image="https://readdy.ai/api/search-image?query=Modern%20business%20partnership%20concept%2C%20multiple%20glowing%20brand%20logos%20floating%20in%20dark%20space%2C%20holographic%20network%20connections%2C%20dramatic%20dark%20moody%20atmosphere%2C%20teal%20and%20amber%20neon%20accents%2C%20cinematic%20technology%20concept%20art%2C%20India%20business%20ecosystem%2C%20highly%20detailed&width=1800&height=800&seq=partners-hero-01&orientation=landscape"
      />

      {/* Brand Partners */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Brand Network
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {brandsHeading}
            </h2>
            <p className="text-foreground-600">
              {brandsSub}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4">
            {brandPartners.map((b) => (
              <div
                key={b.name}
                className="p-4 rounded-xl card-dark text-center group hover:border-primary-500/40 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mx-auto mb-3 group-hover:border-primary-500/60 transition-colors">
                  <i className={`${b.icon} text-lg text-primary-500`} />
                </div>
                <div className="text-sm font-medium text-foreground-950">{b.name}</div>
                <div className="text-xs text-foreground-500 mt-0.5">{b.category}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Developer Partners */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Developer Network
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {devHeading}
            </h2>
            <p className="text-foreground-600">
              Our developer network includes some of India's most respected builders, actively seeking land for JDA and development projects.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {developerPartners.map((d) => (
              <div
                key={d.name}
                className="p-6 rounded-2xl card-dark group hover:border-primary-500/40 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 group-hover:border-primary-500/60 transition-colors shrink-0">
                    <i className="ri-building-4-line text-xl text-primary-500" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-foreground-950">{d.name}</div>
                    <div className="text-xs text-foreground-500">{d.location}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-primary-600">
                  <i className="ri-check-line" />
                  {d.projects} structured
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Join */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              For Partners
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {joinHeading}
            </h2>
            <p className="text-foreground-600">
              Whether you're a brand looking for expansion sites or a developer seeking land — we have the data and the network.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl card-dark text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-store-2-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">
                For Brands
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                Access our database of 10,000+ acres of analyzed land across India. Get location recommendations matched to your expansion criteria.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-500/40 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
              >
                Partner With Us
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="p-8 rounded-2xl card-dark text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-building-2-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">
                For Developers
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                Get pre-vetted land opportunities with AI-backed feasibility reports. Save months of site identification and due diligence.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-500/40 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
              >
                Join Developer Network
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            Submit your land details and get matched with brands and developers actively looking for sites.
          </p>
          <Link
            to="/feasibility-report"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Get Matched With Partners
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}